import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useOptionsApi, useProfileStore } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { useLessonCreateStore } from './create/store'
import { useLessonSlotsApi } from '../../api/lessons/slots.hook'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { getAllowedLessonTypes, getDefaultLessonType } from './lesson-create'

interface LessonCreatePageReturn {
  refIsLoading: ComputedRef<boolean>
}

/** Opens the form on the first offered slot of today, both in the admin view timezone. The page loader covers this
 *  first read alone: the tutors are read under the tutor modal's loader, the save under the card's or the modal's. */
export const useLessonCreatePage = (): LessonCreatePageReturn => {
  const route = useRoute()
  const dayjs = useDayjs()
  const store = useLessonCreateStore()
  const profileStore = useProfileStore()
  const { canManageBookings, canManageDemoBookings } = useLessonPrivileges()

  const refIsLoading = computed<boolean>(() => store.isLoading)

  const getToday = (): string => {
    const now = profileStore.getCurrentTime

    return now
      ? dayjs(now, DateFormat.Default).format(DateFormat.DefaultDate)
      : dayjs().tz(profileStore.getTimezone).format(DateFormat.DefaultDate)
  }

  const init = async (): Promise<void> => {
    store.reset()
    store.setLoading(true)

    // The server clock decides "today"; the one read at sign-in may be hours old.
    const [slots] = await Promise.all([
      useLessonSlotsApi({ disciplineId: route.params.id as string }),
      useOptionsApi(),
    ])

    const today = getToday()
    const allowedTypes = getAllowedLessonTypes({
      canManageBookings: canManageBookings.value,
      canManageDemoBookings: canManageDemoBookings.value,
    })

    store.setSlots(slots)
    store.setType(getDefaultLessonType(allowedTypes))
    store.setToday(today)
    store.setDate(today)
    store.setStartTime(slots?.start[0] ?? '')
    store.setDuration(slots?.duration[0] ?? null)
    store.setLoading(false)
  }

  onMounted(init)

  onBeforeUnmount(() => {
    store.reset()
  })

  return {
    refIsLoading,
  }
}
